// src/massiveRecruitment/MassiveRecruitmentList.js
import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton,
  CircularProgress, Alert, Chip, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  Visibility as ViewIcon, Search as SearchIcon,
  Clear as ClearIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';

const ESTADOS_CONFIG = {
  borrador:  { label: 'Borrador',  color: 'default'  },
  activo:    { label: 'Activo',    color: 'success'  },
  pausado:   { label: 'Pausado',   color: 'warning'  },
  cerrado:   { label: 'Cerrado',   color: 'error'    },
  cubierto:  { label: 'Cubierto',  color: 'info'     },
  cancelado: { label: 'Cancelado', color: 'error'    },
};

const MassiveRecruitmentList = ({ onNew, onEdit, onView }) => {
  const { t } = useTranslation();
  const { authToken, project, showAlert } = useStore();

  const [processes, setProcesses] = useState([]);
  const [filteredProcesses, setFilteredProcesses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [processToDelete, setProcessToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAll = async () => {
    const response = await api.get(`/massive-recruitments`, {
      params: { projectId: project },
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  };

  const deleteProcess = async (id) => {
    const response = await api.delete(`/massive-recruitments/${id}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  };

  const loadProcesses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchAll();
      if (response.response?.status_response === 'OK') {
        setProcesses(response.response.data || []);
      } else {
        setError(t('massiveRecruitment.loadError'));
        showAlert(t('massiveRecruitment.loadError'), 'warning');
      }
    } catch (err) {
      setError(t('generalApiError'));
      showAlert(t('generalApiError'), 'error');
      console.error('Error loading massive recruitments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (project) loadProcesses(); }, [project]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProcesses(processes);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredProcesses(processes.filter((p) =>
        p.nombre?.toLowerCase().includes(term) ||
        p.estado?.toLowerCase().includes(term)
      ));
    }
  }, [processes, searchTerm]);

  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    try {
      const date = dateValue?._seconds
        ? new Date(dateValue._seconds * 1000)
        : new Date(dateValue);
      return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return '-'; }
  };

  const handleDeleteClick = (process) => {
    setProcessToDelete(process);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      const response = await deleteProcess(processToDelete.uid);
      if (response.response?.status_response === 'OK') {
        showAlert(t('massiveRecruitment.deleteSuccess'), 'success');
        setProcesses((prev) => prev.filter((p) => p.uid !== processToDelete.uid));
      } else {
        showAlert(t('massiveRecruitment.deleteError'), 'error');
      }
    } catch (err) {
      showAlert(t('generalApiError'), 'error');
      console.error('Error deleting massive recruitment:', err);
    } finally {
      setDeleting(false);
      setOpenDeleteDialog(false);
      setProcessToDelete(null);
    }
  };

  if (loading && processes.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">{t('massiveRecruitment.list.title')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder={t('massiveRecruitment.list.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: 260 }}
          />
          <IconButton onClick={loadProcesses} disabled={loading} title={t('refresh')}>
            <RefreshIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={onNew}>
            {t('massiveRecruitment.list.newProcess')}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>{t('name')}</strong></TableCell>
              <TableCell><strong>{t('massiveRecruitment.list.vacantes')}</strong></TableCell>
              <TableCell><strong>{t('massiveRecruitment.list.estado')}</strong></TableCell>
              <TableCell><strong>{t('massiveRecruitment.list.prioridad')}</strong></TableCell>
              <TableCell><strong>{t('massiveRecruitment.list.fechaInicio')}</strong></TableCell>
              <TableCell><strong>{t('massiveRecruitment.list.fechaCierre')}</strong></TableCell>
              <TableCell><strong>{t('createdAt')}</strong></TableCell>
              <TableCell><strong>{t('actions')}</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProcesses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {searchTerm ? t('massiveRecruitment.list.noResults') : t('massiveRecruitment.list.empty')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredProcesses.map((process) => {
                const estadoConfig = ESTADOS_CONFIG[process.estado] || { label: process.estado, color: 'default' };
                return (
                  <TableRow key={process.uid} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{process.nombre}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {process.vacantesContratadas ?? 0} / {process.vacantesRequeridas ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={estadoConfig.label} color={estadoConfig.color} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip label={process.prioridad ?? '-'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(process.fechaInicioBusqueda)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(process.fechaCierreBusqueda)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(process.created)}</Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" title={t('show')} onClick={() => onView(process)}>
                        <ViewIcon />
                      </IconButton>
                      <IconButton size="small" title={t('edit')} onClick={() => onEdit(process)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" title={t('delete')} color="error" onClick={() => handleDeleteClick(process)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('massiveRecruitment.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('massiveRecruitment.deleteConfirm')} <strong>"{processToDelete?.nombre}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('noWayBack')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="inherit">{t('cancel')}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MassiveRecruitmentList;